import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Calendar, Users, Clock, CheckSquare, Settings,
  BarChart3, LogOut, Menu, MapPin, CalendarDays,
  Shield, Building2, Network, LifeBuoy, Banknote, LayoutDashboard, ChevronDown
} from 'lucide-react';
import { Button } from './ui/button';
import Logo from './Logo';
import ThemeToggle from './ThemeToggle';
import { useAuth } from '../context/AuthContext';
import { dashboardApi, supportApi, toilApi } from '../lib/api';

const Sidebar = ({ sidebarOpen, setSidebarOpen, refreshTrigger }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, tenant } = useAuth();
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [supportUnread, setSupportUnread] = useState(0);
  // Time in lieu claims this person may sign off (DESIGN-toil.md) count on the Approvals badge.
  const [toilWaiting, setToilWaiting] = useState(0);
  // "There's more below" (Rob's 1A, 29/10/2026): on a short screen the menu scrolls, and
  // nothing showed it — Holidays, Reports, Settings and Payroll simply weren't there.
  const navRef = useRef(null);
  const [moreBelow, setMoreBelow] = useState(false);
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;
    const check = () => setMoreBelow(nav.scrollTop + nav.clientHeight < nav.scrollHeight - 4);
    check();
    nav.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(check) : null;
    if (ro) ro.observe(nav);
    return () => {
      nav.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
      if (ro) ro.disconnect();
    };
  }, []);
  useEffect(() => {
    if (!(user?.role === 'manager' || user?.role === 'admin' || user?.role === 'superadmin')) return;
    const fetchToil = () =>
      toilApi.pending().then((r) => setToilWaiting((r.data?.claims || []).length)).catch(() => {});
    fetchToil();
    window.addEventListener('toil:pending-changed', fetchToil);
    return () => window.removeEventListener('toil:pending-changed', fetchToil);
  }, [user, location.pathname, refreshTrigger]);

  // Fetch pending approvals count for badge
  useEffect(() => {
    const fetchPendingApprovals = async () => {
      if (user?.role === 'manager' || user?.role === 'admin' || user?.role === 'superadmin') {
        try {
          const response = await dashboardApi.getPendingApprovals();
          setPendingApprovals(response.data || []);
        } catch (error) {
          console.error('Failed to fetch pending approvals:', error);
        }
      }
    };
    fetchPendingApprovals();
  }, [user, refreshTrigger]); // Re-fetch when refreshTrigger changes

  // Unread support replies badge — refetch on nav AND when a ticket is opened
  // (the Support page fires 'support:refresh-unread' after marking one read).
  useEffect(() => {
    if (!user) return;
    const fetchUnread = () =>
      supportApi.unreadCount().then((r) => setSupportUnread(r.data?.unread || 0)).catch(() => {});
    fetchUnread();
    window.addEventListener('support:refresh-unread', fetchUnread);
    return () => window.removeEventListener('support:refresh-unread', fetchUnread);
  }, [user, location.pathname, refreshTrigger]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Role-based navigation
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const isManager = user?.role === 'manager';
  
  // Build navigation items based on role
  const navItems = [];

  // Superadmin link at top
  if (user?.role === 'superadmin') {
    navItems.push({ 
      icon: <Shield className="w-5 h-5" />, 
      label: 'Superadmin', 
      path: '/superadmin',
      highlight: true 
    });
  }

  // Base nav items for all users
  navItems.push(
    { icon: <LayoutDashboard className="w-5 h-5" />, label: 'Dashboard', path: '/dashboard' },
    // The wallchart's own page, second for every role (Warm Hive decision 9; Rob's 2A,
    // 29/10/2026: it takes the calendar icon, so Dashboard got one of its own).
    { icon: <Calendar className="w-5 h-5" />, label: 'Team calendar', path: '/team-calendar' },
    { icon: <Clock className="w-5 h-5" />, label: 'My leave', path: '/leave' },
    { icon: <LifeBuoy className="w-5 h-5" />, label: 'Support', path: '/support', badge: supportUnread },
  );
  
  // Managers: Approvals + My Team (+ Reports with the permission below).
  // ⚠️ Org Chart and Holidays are ADMIN pages (Rob, 29/09/2026, option A). Company-wide
  // managers used to be offered both — carried over from the retired Director role in
  // July — but both routes are AdminRoute, so the links sent them back to the dashboard.
  // lib/managerMenuMatchesRoutes.checks.mjs fails if a manager is offered a page they
  // can't open again.
  if (isManager) {
    navItems.push(
      { icon: <CheckSquare className="w-5 h-5" />, label: 'Approvals', path: '/approvals', badge: pendingApprovals.length + toilWaiting },
      { icon: <Users className="w-5 h-5" />, label: 'My team', path: '/employees' }
    );
    // Reports follows the "Can view reports" PERMISSION, not company-wide reach
    // (DESIGN-reports.md C). How far they see is their reach, decided on the server.
    // ⚠️ A menu hint only — every report route checks the permission itself.
    if (user?.can_view_reports) {
      navItems.push(
        { icon: <BarChart3 className="w-5 h-5" />, label: 'Reports', path: '/reports' }
      );
    }
  }
  
  // Admin-only items (full access including Settings)
  if (isAdmin) {
    navItems.push(
      { 
        icon: <CheckSquare className="w-5 h-5" />, 
        label: 'Approvals', 
        path: '/approvals', 
        badge: pendingApprovals.length + toilWaiting 
      },
      { icon: <MapPin className="w-5 h-5" />, label: 'Locations', path: '/locations' },
      { icon: <Building2 className="w-5 h-5" />, label: 'Departments', path: '/employees?tab=departments' },
      { icon: <Users className="w-5 h-5" />, label: 'Employees', path: '/employees' },
      { icon: <Network className="w-5 h-5" />, label: 'Org chart', path: '/org-chart' },
      { icon: <CalendarDays className="w-5 h-5" />, label: 'Holidays', path: '/holidays' },
      { icon: <BarChart3 className="w-5 h-5" />, label: 'Reports', path: '/reports' },
      { icon: <Settings className="w-5 h-5" />, label: 'Settings', path: '/settings' },
    );
  }

  // Payroll sits OUTSIDE the role blocks on purpose (DESIGN-payroll.md).
  // Sandra in accounts is an ordinary employee who runs payroll, so the nav
  // entry follows the PERMISSION, not the role — putting it inside `isAdmin`
  // would hide it from the person the feature exists for.
  // ⚠️ This is a menu hint only. /payroll and every payroll endpoint check the
  // permission themselves; a hidden nav item has never protected anything.
  if (user?.can_run_payroll) {
    navItems.push(
      { icon: <Banknote className="w-5 h-5" />, label: 'Payroll', path: '/payroll' },
    );
  }

  // Check if current path matches nav item (handle query params for tabs)
  const isActive = (path) => {
    const currentPath = location.pathname;
    const currentSearch = location.search;
    const [itemPath, itemQuery] = path.split('?');
    
    // If paths don't match, not active
    if (currentPath !== itemPath) return false;
    
    // If the nav item has a query param (like ?tab=departments)
    if (itemQuery) {
      // Only active if the current URL has the same query param
      return currentSearch.includes(itemQuery);
    }
    
    // If nav item has no query param, only active if current URL also has no tab param
    // This prevents /employees from being active when on /employees?tab=departments
    if (currentSearch.includes('tab=')) {
      return false;
    }
    
    return true;
  };

  return (
    <>
      <aside className={`fixed lg:sticky inset-y-0 top-[var(--hh-dock,0px)] lg:top-[var(--hh-dock,0px)] lg:h-[calc(100vh_-_var(--hh-dock,0px))] left-0 z-50 w-64 bg-card border-r border-border transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-4 pb-3 border-b border-border">
            <Link to="/dashboard" data-testid="sidebar-logo">
              <Logo size={30} />
            </Link>
            {/* Which company you're signed in to (Rob, 28/10/2026: "there is no indication
                to which company you are signed in to"). Words only — nothing to press. */}
            {tenant?.company_name && (
              <div className="mt-3 flex items-center gap-2.5 rounded-xl border border-border bg-field px-3 py-2" data-testid="sidebar-company">
                <Building2 className="w-4 h-4 text-hive-orange flex-shrink-0" />
                <div className="min-w-0 leading-tight">
                  <p className="text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">Signed in to</p>
                  <p className="font-heading text-sm font-semibold truncate" title={tenant.company_name}>{tenant.company_name}</p>
                </div>
              </div>
            )}
          </div>
          
          <nav ref={navRef} className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3.5 py-2 rounded-full font-heading text-[14.5px] font-medium transition-colors ${
                  isActive(item.path)
                    ? 'bg-primary text-primary-foreground font-semibold'
                    : item.highlight 
                      ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                {item.icon}
                <span>{item.label}</span>
                {item.badge > 0 && (
                  <span className="ml-auto bg-destructive text-destructive-foreground text-xs px-2 py-0.5 rounded-full">
                    {item.badge}
                  </span>
                )}
              </Link>
            ))}
            {/* Only while there's more of the menu below; a click scrolls to it, and it goes
                once you reach the bottom (Rob's 1A, 29/10/2026). */}
            {moreBelow && (
              <div className="sticky -bottom-4 -mx-4 -mb-4 h-12 flex items-end justify-center pb-1.5 bg-gradient-to-b from-transparent to-card to-70% pointer-events-none">
                <button
                  type="button"
                  onClick={() => navRef.current?.scrollBy({ top: navRef.current.clientHeight * 0.8, behavior: 'smooth' })}
                  className="pointer-events-auto w-7 h-7 rounded-full bg-hive-orange text-hive-navy flex items-center justify-center shadow-md hover:bg-hive-orange-light transition-colors"
                  aria-label="More menu items below"
                  title="More below"
                  data-testid="sidebar-more-below"
                >
                  <ChevronDown className="w-4 h-4" strokeWidth={2.6} />
                </button>
              </div>
            )}
          </nav>
          
          {/* User info at bottom */}
          <div className="p-4 border-t border-border">
            {/*
              ⚠️ THE ONLY WAY INTO /profile FROM ANYWHERE IN THE APP.

              Until 18/08/2026 there was none at all: the route existed, the page rendered,
              and nothing in the entire frontend linked to it — so it could only be reached
              by typing the URL. Rob had never seen the page. It surfaced because two-factor
              enrolment was put there, i.e. a security feature nobody could navigate to.

              ⚠️ This is the same shape as the bug on 13/08 where the onboarding wizard was
              only safe because nothing linked into it. **A page nothing links to is not a
              decision, it is an oversight that happens to be quiet.**
            */}
            <Link
              to="/profile"
              onClick={() => setSidebarOpen(false)}
              className="flex items-center gap-3 mb-3 rounded-xl p-1.5 -m-1.5 hover:bg-secondary transition-colors"
              data-testid="sidebar-profile-link"
              title="Your profile and security settings"
            >
              {user?.profile_picture_url ? (
                <img
                  src={user.profile_picture_url}
                  alt={`${user?.first_name} ${user?.last_name}`}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-hive-orange to-hive-annual flex items-center justify-center text-hive-navy font-bold">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-heading font-semibold truncate">{user?.first_name} {user?.last_name}</p>
                {/* The email moved to the Profile page (Rob, 28/10/2026: it was cut off
                    here). This line says what the link does — before, nothing did. */}
                <p className="text-sm font-semibold text-hive-burnt dark:text-hive-orange">My profile &rsaquo;</p>
              </div>
            </Link>
            <div className="grid grid-cols-2 gap-2">
              <ThemeToggle variant="outline" className="w-full px-2 text-xs font-bold text-muted-foreground hover:text-foreground whitespace-nowrap" />
              <Button
                variant="outline"
                className="w-full px-2 text-xs font-bold text-muted-foreground hover:text-foreground whitespace-nowrap"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-1.5" />
                Sign out
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </>
  );
};

export default Sidebar;
